import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// attach bearer token from localStorage if present (fallback to cookie)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("apex_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join("; ");
  return typeof d === "object" && d.msg ? d.msg : JSON.stringify(d);
}

export const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const roleLabel = (r) =>
  ({ borrower: "Borrower", broker: "Broker", loan_rep: "Loan Rep", admin: "Administrator" }[r] || r);

/**
 * Fetch a document from an authenticated endpoint and open it in a new tab.
 * Solves the "<a href> can't attach Bearer token" 401/403 problem.
 * @param {string} path       api-relative path, e.g. `/documents/abc/download`
 * @param {string} filename   optional filename to use if the user Ctrl-clicks "save as"
 */
async function _errFromBlobResponse(err) {
  // When responseType=blob, axios delivers 4xx/5xx bodies as a Blob.
  // Decode & parse so users see the server's helpful message instead of a
  // useless "Request failed with status code 500".
  try {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return formatApiError({ response: { data: parsed } });
      } catch {
        if (text) return text.slice(0, 300);
      }
    }
  } catch { /* ignore */ }
  return formatApiError(err);
}

export async function openAuthorizedDocument(path, filename) {
  // Open the blank tab synchronously so mobile Safari / most popup blockers allow it
  const win = window.open("", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = res.data;
    const url = URL.createObjectURL(blob);
    if (win && !win.closed) {
      win.location.href = url;
    } else {
      // Popup blocked — fall back to a synthetic download
      const a = document.createElement("a");
      a.href = url;
      if (filename) a.download = filename;
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoke the object URL after a delay so the new tab has time to load
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    if (win && !win.closed) win.close();
    throw new Error(await _errFromBlobResponse(err));
  }
}

/**
 * Download a document to disk (forces the browser's download UI).
 */
export async function downloadAuthorizedDocument(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    throw new Error(await _errFromBlobResponse(err));
  }
}
